import React, { useState, useEffect } from 'react';
import { Plus, Search, Pencil, Trash2, Building2, MapPin, AlertCircle, Gavel } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from '@/components/ui/use-toast';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddressWithMap from '@/components/AddressWithMap';

const INITIAL_FORUM_STATE = {
  id: '',
  nome: '',
  estado: '',
  cidade: '',
};

const INITIAL_VARA_STATE = {
  id: '',
  nome: '',
};

const ForumManager = () => {
  const [forums, setForums] = useState([]);
  const [varas, setVaras] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [isForumModalOpen, setIsForumModalOpen] = useState(false);
  const [isVaraModalOpen, setIsVaraModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Forms
  const [forumFormData, setForumFormData] = useState(INITIAL_FORUM_STATE);
  const [varaFormData, setVaraFormData] = useState(INITIAL_VARA_STATE);
  
  // Active Tab
  const [activeTab, setActiveTab] = useState("foruns");

  useEffect(() => {
    const storedForums = localStorage.getItem('foruns');
    const storedVaras = localStorage.getItem('varas');
    if (storedForums) setForums(JSON.parse(storedForums));
    if (storedVaras) setVaras(JSON.parse(storedVaras));
  }, []);

  // --- Forum Handlers ---
  const handleForumSave = (e) => {
    e.preventDefault();
    if (!forumFormData.nome || !forumFormData.estado || !forumFormData.cidade) {
      toast({ title: "Campos obrigatórios faltando", variant: "destructive" });
      return;
    }

    let updatedList;
    if (isEditMode) {
      updatedList = forums.map(item => item.id === forumFormData.id ? forumFormData : item);
    } else {
      updatedList = [...forums, { ...forumFormData, id: crypto.randomUUID() }];
    }

    setForums(updatedList);
    localStorage.setItem('foruns', JSON.stringify(updatedList));
    setIsForumModalOpen(false);
    toast({ title: isEditMode ? "Fórum atualizado!" : "Fórum criado!" });
  };

  const handleForumDelete = (id) => {
    const updated = forums.filter(f => f.id !== id);
    setForums(updated);
    localStorage.setItem('foruns', JSON.stringify(updated));
    toast({ title: "Fórum removido." });
  };

  const openForumModal = (forum = null) => {
    if (forum) {
      setForumFormData(forum);
      setIsEditMode(true);
    } else {
      setForumFormData(INITIAL_FORUM_STATE);
      setIsEditMode(false);
    }
    setIsForumModalOpen(true);
  };

  // --- Vara Handlers ---
  const handleVaraSave = (e) => {
    e.preventDefault();
    if (!varaFormData.nome) {
      toast({ title: "Nome da Vara obrigatório", variant: "destructive" });
      return;
    }

    let updatedList;
    if (isEditMode) {
      updatedList = varas.map(item => item.id === varaFormData.id ? varaFormData : item);
    } else {
      updatedList = [...varas, { ...varaFormData, id: crypto.randomUUID() }];
    }

    setVaras(updatedList);
    localStorage.setItem('varas', JSON.stringify(updatedList));
    setIsVaraModalOpen(false);
    toast({ title: isEditMode ? "Vara atualizada!" : "Vara criada!" });
  };

  const handleVaraDelete = (id) => {
    const updated = varas.filter(v => v.id !== id);
    setVaras(updated);
    localStorage.setItem('varas', JSON.stringify(updated));
    toast({ title: "Vara removida." });
  };

  const openVaraModal = (vara = null) => {
    if (vara) {
      setVaraFormData(vara);
      setIsEditMode(true);
    } else {
      setVaraFormData(INITIAL_VARA_STATE);
      setIsEditMode(false);
    }
    setIsVaraModalOpen(true);
  };

  // --- Filtering ---
  const filteredForums = forums.filter(f => 
    f.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.cidade.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredVaras = varas.filter(v => 
    v.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const brazilianStates = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
    'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
  ];

  return (
    <div className="container mx-auto px-4 max-w-5xl animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Building2 className="w-8 h-8 text-indigo-600" />
            Gestão Judiciária
          </h1>
          <p className="text-slate-500">Cadastre e gerencie Fóruns e Varas.</p>
        </div>
        <Button 
          onClick={() => activeTab === 'foruns' ? openForumModal() : openVaraModal()} 
          className="bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" /> 
          {activeTab === 'foruns' ? 'Novo Fórum' : 'Nova Vara'}
        </Button>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input 
              placeholder={activeTab === 'foruns' ? "Buscar fórum ou cidade..." : "Buscar vara..."}
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="foruns" value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="foruns" className="flex gap-2"><Building2 className="w-4 h-4" /> Fóruns</TabsTrigger>
          <TabsTrigger value="varas" className="flex gap-2"><Gavel className="w-4 h-4" /> Varas</TabsTrigger>
        </TabsList>

        <TabsContent value="foruns">
          <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-700 border-b">
                <tr>
                  <th className="px-6 py-4">Nome do Fórum</th>
                  <th className="px-6 py-4">Localização</th>
                  <th className="px-6 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredForums.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <AlertCircle className="w-8 h-8 text-slate-300" />
                        <p>Nenhum fórum encontrado.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredForums.map((forum) => (
                    <tr key={forum.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium">
                        <div>{forum.nome}</div>
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        <AddressWithMap 
                          text={`${forum.cidade} - ${forum.estado}`} 
                          data={{ cidade: forum.cidade, uf: forum.estado, logradouro: forum.nome }} 
                        />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => openForumModal(forum)}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleForumDelete(forum.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="varas">
           <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-700 border-b">
                <tr>
                  <th className="px-6 py-4">Nome da Vara</th>
                  <th className="px-6 py-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredVaras.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <Gavel className="w-8 h-8 text-slate-300" />
                        <p>Nenhuma vara encontrada.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredVaras.map((vara) => (
                    <tr key={vara.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium">
                        <div>{vara.nome}</div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <Button variant="ghost" size="icon" onClick={() => openVaraModal(vara)}>
                          <Pencil className="w-4 h-4 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleVaraDelete(vara.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Forum Modal */}
      <Dialog open={isForumModalOpen} onOpenChange={setIsForumModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Editar Fórum' : 'Novo Fórum'}</DialogTitle>
            <DialogDescription>Preencha os dados do fórum.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleForumSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome do Fórum *</Label>
              <Input 
                value={forumFormData.nome} 
                onChange={(e) => setForumFormData({...forumFormData, nome: e.target.value})} 
                placeholder="Ex: Fórum João Mendes"
                required 
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>Cidade *</Label>
                <Input 
                  value={forumFormData.cidade} 
                  onChange={(e) => setForumFormData({...forumFormData, cidade: e.target.value})} 
                  required 
                />
              </div>
              <div className="space-y-2">
                <Label>Estado *</Label>
                <Select 
                  value={forumFormData.estado} 
                  onValueChange={(v) => setForumFormData({...forumFormData, estado: v})}
                >
                  <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>
                    {brazilianStates.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
               <Button type="submit">Salvar Fórum</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vara Modal */}
      <Dialog open={isVaraModalOpen} onOpenChange={setIsVaraModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Editar Vara' : 'Nova Vara'}</DialogTitle>
            <DialogDescription>Cadastre uma nova vara para usar nos processos.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleVaraSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Vara *</Label>
              <Input 
                value={varaFormData.nome} 
                onChange={(e) => setVaraFormData({...varaFormData, nome: e.target.value})} 
                placeholder="Ex: 1ª Vara Cível"
                required 
              />
            </div>
            <DialogFooter>
               <Button type="submit">Salvar Vara</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default ForumManager;